import { apiFetch, type ApiTransportDeps } from './http';
import { apiBaseUrl } from './baseUrl';
import { Storage } from '../storage';
import { clearPadLastKnown } from '../banepadLastKnown';
import {
  clearBanepadDevicePairing,
  isBanepadDevicePaired,
  legacyDeviceToken,
  markBanepadDevicePaired,
  retireLegacyDeviceToken,
} from '../banepadDeviceSession';

// ─────────────────────────────────────────────────────────────────────
// THE PAD'S OWN TRANSPORT (#185).
//
// A paired Banepad is a THIRD subject, beside the club user and the member —
// and unlike them it is not a person. It authenticates with a device token
// (`spd_`) that the SERVER mints at pairing and stores only as a hash.
//
// DEVICE AUTH IS DELIBERATELY NOT IN THE GENERIC SUBJECT FALLBACK.
//
// `defaultDeps()` in http.ts attaches club → member → Entra, and a device
// belongs nowhere in that chain. Adding it would mean every request the bundle
// makes — a portal screen, a member's booking, anything — would carry a pad's
// credential whenever a tablet happened to have one in localStorage. A device
// token authorises exactly four endpoints about exactly one court; attaching it
// by default would be handing it to code that has no business holding it.
//
// So pad calls opt IN, explicitly, through these deps. Nothing else in the app
// can reach the device token by accident, because nothing else asks for it.
// ─────────────────────────────────────────────────────────────────────

/** Is the pad talking to the API rather than the legacy blob store? */
export function padApiModeActive(): boolean {
  return Storage.backend === 'http';
}

/**
 * Transport deps for THIS pad, and nothing else.
 *
 * ── THE CREDENTIAL IS THE COOKIE NOW (#663) ─────────────────────────
 *
 * `spd_session` is httpOnly, so there is no token to pass in and nothing here
 * can read one. `credentials: 'include'` in `apiFetch` is what carries it.
 *
 * `getToken` returns the LEGACY token while one is still in storage — the
 * transitional bearer for a tablet paired before the cookie existed. `apiFetch`
 * attaches the header if and only if a token is supplied, so a pad with no legacy
 * token sends no `Authorization` at all, which is what lets the cookie decide.
 * That matters: `requireDeviceAuth` refuses a present-but-invalid header rather
 * than falling through to the cookie, so an empty bearer would be a 401.
 *
 * `onUnauthorized` fires only when WE attached a token, so it covers the
 * transitional phase only. The cookie phase is covered by `fetchPadIdentity`
 * below — see the note there.
 */
export function padTransportDeps(): Partial<ApiTransportDeps> {
  return {
    baseUrl: apiBaseUrl(),
    getToken: async () => legacyDeviceToken(),
    // A pad names no tenant: its club comes from the device record, server-side.
    getTenantId: () => null,
    fetchImpl: (...args: Parameters<typeof fetch>) => fetch(...args),
    onUnauthorized: () => {
      clearBanepadDevicePairing();
      // The court's last-known note goes with the token (#479): once the
      // authority has rejected this device it is not that court's pad any
      // more, so there is no reason for its players' names to stay on the
      // tablet. `readPadLastKnown` would already refuse a re-pairing to a
      // different court — this is about not keeping it at all.
      clearPadLastKnown();
    },
  };
}

/** What the server says this pad is. */
export interface PadIdentity {
  deviceId: string;
  court: { id: number; name: string };
  club: { id?: string; slug?: string; name?: string };
}

/**
 * A STRING discriminant, not a boolean: this repo compiles with `strict: false`,
 * where a boolean-literal union does not narrow and `out.error` would not
 * type-check on the refused branch.
 */
export type PadPairOutcome =
  // NO `token` (#663): the credential arrives as an httpOnly cookie the browser
  // stores and this code cannot read. What comes back is WHO the pad is.
  | { status: 'paired'; identity: PadIdentity }
  | { status: 'refused'; error: 'invalid_code' | 'throttled' | 'unreachable' };

/**
 * Exchange a pairing code for a paired device.
 *
 * The code is the weak, short-lived half; the secret is the token, and this
 * function never sees it. The server mints it, stores only its hash, and sends it
 * as an httpOnly `spd_session` cookie — so the response read here says who the pad
 * is and nothing about how it will authenticate (#663).
 */
export async function pairPadDevice(code: string): Promise<PadPairOutcome> {
  let res: Response;
  try {
    res = await apiFetch('/pad/pair', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    });
  } catch {
    return { status: 'refused', error: 'unreachable' };
  }
  if (res.status === 429) return { status: 'refused', error: 'throttled' };
  if (!res.ok) {
    // The server answers ONE error for a wrong, expired, revoked or unknown
    // code, so the endpoint cannot be used to probe which codes exist. Keeping
    // that single answer here means the screen cannot leak the difference
    // either.
    return { status: 'refused', error: 'invalid_code' };
  }
  const body = (await res.json().catch(() => null)) as Record<string, any> | null;
  // ── THE SUCCESS SIGNAL IS THE CONTRACT, NOT A TOKEN (#663) ──────
  //
  // This used to read `body.token` and treat its presence as proof of pairing. The
  // body carries no credential any more, so the signal is what the response does
  // promise: `{ deviceId, court, club }`.
  //
  // BOTH REQUIRED FIELDS ARE CHECKED (#670 review). An earlier version took a
  // court alone as sufficient, which is a client that accepts less than the
  // documented contract — and `deviceId` is not cosmetic: the pad identifies
  // itself by it in its heartbeat, so pairing «successfully» without one gives a
  // pad that beats under an empty id. That exact defect is why the field was added
  // to this response in the first place.
  //
  // It is not an authority check and must not be read as one: the cookie and the
  // device row remain authoritative, and every later request re-resolves both.
  // This is about refusing a response the server should never send.
  const courtId = Number(body?.court?.id) || 0;
  const deviceId = String(body?.deviceId ?? '');
  if (!body || courtId === 0 || deviceId === '') {
    return { status: 'refused', error: 'invalid_code' };
  }
  return {
    status: 'paired',
    identity: {
      deviceId: String(body.deviceId ?? ''),
      court: { id: Number(body.court?.id) || 0, name: String(body.court?.name ?? '') },
      club: { slug: body.club?.slug, name: body.club?.name },
    },
  };
}

/**
 * Ask the server which court this pad is.
 *
 * Replaces downloading the club's whole device registry and matching the token
 * in the browser. The court comes from the device record the token resolves to,
 * so a pad cannot claim a court by editing anything it holds.
 *
 * Returns null when the server refuses — revoked, unknown, or no token — which
 * is the pad's error state.
 */
export async function fetchPadIdentity(): Promise<PadIdentity | null> {
  let res: Response;
  try {
    res = await apiFetch('/pad/me', undefined, padTransportDeps() as ApiTransportDeps);
  } catch {
    // A network failure is NOT a revocation. Returning null here would move a
    // pad with a perfectly good credential to its error screen because the wifi
    // blinked, so it is reported as "unknown" by throwing to the caller.
    throw new Error('[pad] /pad/me unreachable');
  }
  // ── THE AUTHORITY REFUSED US, SO THE TABLET LETS GO (#663) ──────
  //
  // This used to be `onUnauthorized`'s job, and `onUnauthorized` fires only when
  // WE attached a token — which, once the credential is a cookie, is never. The
  // cleanup would simply have stopped happening, leaving a revoked tablet holding
  // the court's last-known note with its players' names on screen.
  //
  // 401 ONLY, not `!res.ok`: a 403 is the CSRF refusal and says nothing about
  // whether this pad is still paired. The server clears the cookie itself on a
  // 401 (#666); this is the device-local half of the same event.
  if (res.status === 401) {
    clearBanepadDevicePairing();
    clearPadLastKnown();
    return null;
  }
  if (!res.ok) return null;
  const body = (await res.json().catch(() => null)) as Record<string, any> | null;
  if (!body) return null;
  return {
    deviceId: String(body.deviceId ?? ''),
    court: { id: Number(body.court?.id) || 0, name: String(body.court?.name ?? '') },
    club: { id: body.club?.id },
  };
}

/**
 * Report that this pad is alive.
 *
 * WHAT IT NO LONGER DOES is the point. The pad used to write its own heartbeat
 * record into the club's key/value store — a device writing club data, under a
 * key it chose, with a court id and a device id it supplied. Presence was
 * therefore whatever the tablet claimed it was, and any client that could
 * write /kv could claim it for any pad.
 *
 * Now the pad sends two diagnostic strings and nothing else. WHICH device is
 * alive is resolved from the token, server-side, so a pad can only ever report
 * ITSELF — and it has no /kv authority at all.
 *
 * Throws when the server cannot be reached, because a missed beat is what the
 * caller measures an outage with; swallowing it would report a dead pad as
 * healthy.
 */
export async function postPadHeartbeat(body: {
  appVersion?: string;
  shell?: 'app' | 'browser';
}): Promise<void> {
  const res = await apiFetch(
    '/pad/heartbeat',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
    padTransportDeps() as ApiTransportDeps,
  );
  if (!res.ok) throw new Error('[pad] heartbeat refused: ' + res.status);
}

// ─────────────────────────────────────────────────────────────────────
// RECORDING A RESULT (#185).
//
// The pad sends THE SCORE THE OPERATOR ENTERED and nothing else. It does not
// send a finished match, and it does not compute one: which match this is, what
// the result means for the table, and what it does to anybody's rating are the
// server's to decide, with the club's stored engine settings, inside one
// transaction.
//
// That is a real change for KM and seriespill, whose pads used to build the
// completed match locally and hand it over. The screen may still show the score
// the operator typed — that is their input, and taking it away while the
// network settles would be worse than useless — but nothing committed is read
// back from it.
// ─────────────────────────────────────────────────────────────────────

/** The three score shapes the formats actually use. */
export type PadResultScore =
  /** padel24 and the americano family: two numbers. */
  | { kind: 'points'; score1: number; score2: number }
  /** klubbmesterskap and seriespill: a score object built from entered sets. */
  | { kind: 'sets'; score: Record<string, unknown> }
  /** enkeltmatch: the sets themselves. */
  | { kind: 'singleMatch'; sets: unknown[] };

/**
 * A retry must carry the SAME id as the attempt it repeats, and a genuinely new
 * submission must not collide with it. So the id is DERIVED from the operation
 * — the match and the score — never generated (CLAUDE.md).
 *
 * Deterministic on purpose: the pad does not have to remember an id across a
 * reload or a crash to retry safely. It recomputes the same one from the same
 * entry.
 */
export function padResultRequestId(matchId: string, score: PadResultScore): string {
  const part =
    score.kind === 'points'
      ? `p:${score.score1}-${score.score2}`
      : score.kind === 'sets'
        ? `s:${stableJson(score.score)}`
        : `m:${stableJson(score.sets)}`;
  return `${matchId}|${part}`;
}

/** JSON with object keys sorted, so key order cannot change the derived id. */
function stableJson(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null';
  if (Array.isArray(value)) return `[${value.map(stableJson).join(',')}]`;
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, v]) => v !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${stableJson(v)}`).join(',')}}`;
}

/**
 * What the server said about the submission.
 *
 * A STRING discriminant: this repo compiles with `strict: false`, where a
 * boolean-literal union does not narrow.
 *
 *   applied     the club's state now includes this result. `view` is the
 *               server's own court view — the only thing the pad may render as
 *               committed.
 *   refused     the server answered and said no. Retrying will not help; the
 *               pad must re-read the court and show what is actually there.
 *   unreachable we do not know. The submission may or may not have landed, so
 *               it stays pending and is retried with the SAME id.
 */
export type PadResultOutcome =
  | { status: 'applied'; view: Record<string, unknown> }
  | { status: 'refused'; error: string; httpStatus: number }
  | { status: 'unreachable' };

/** Send one result. The response body is the server's court view. */
/**
 * Statuses that mean «we could not ask», not «the server considered it and
 * said no» (#371).
 *
 * WHY THIS IS AN ALLOW-LIST AND NOT `status >= 500`.
 *
 * `/pad/result` answers **501 `unsupported_session`** deliberately, in three
 * places — a court whose session this command cannot finalise. Treating every
 * 5xx as transient would retry that every five seconds for ever, against a pad
 * that is never going to be supported. The deliberate refusals are 400, 401,
 * 409, 422 and 501; what is left is the server failing, and only that is
 * retried.
 *
 * WHY IT MATTERS. A retry is SAFE here — the request id is derived from the
 * match and the score, so the server recognises its own work and answers with
 * the court view rather than applying anything twice. That is the whole design.
 * Filing a 500 as a refusal threw the result away and told the operator it had
 * been rejected, at exactly the moment the platform was unhealthy and the queue
 * was most needed.
 *
 * Found during the #216 rehearsal, by stopping the API mid-submission. It did
 * not reproduce then only because the pad is cross-origin: the platform's error
 * page carries no CORS headers, so `fetch` threw and landed in the catch above.
 * A 5xx from our own Hono app carries them, and takes this path instead.
 */
const COULD_NOT_ASK = new Set([
  408, // request timeout — the answer never arrived
  429, // throttled; the retry timer is the right response
  500, // an unexpected failure in the command, rolled back
  502,
  503,
  504, // gateway, restarting container, upstream timeout
]);

/** Is `status` a server failure the pad should retry rather than report? */
export function isRetryableStatus(status: number): boolean {
  return COULD_NOT_ASK.has(status);
}

export async function submitPadResult(
  matchId: string,
  score: PadResultScore,
): Promise<PadResultOutcome> {
  const payload: Record<string, unknown> = {
    matchId,
    requestId: padResultRequestId(matchId, score),
  };
  if (score.kind === 'points') {
    payload.score1 = score.score1;
    payload.score2 = score.score2;
  } else if (score.kind === 'sets') {
    payload.score = score.score;
  } else {
    payload.sets = score.sets;
  }

  let res: Response;
  try {
    res = await apiFetch(
      '/pad/result',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      },
      padTransportDeps() as ApiTransportDeps,
    );
  } catch {
    // COULD NOT ASK — not a refusal. The result may well have been applied and
    // the answer lost, which is exactly the case `requestId` exists for.
    return { status: 'unreachable' };
  }

  if (res.ok) {
    const view = (await res.json().catch(() => null)) as Record<string, unknown> | null;
    // A 200 with no body is still an application; the pad re-reads instead.
    return { status: 'applied', view: view ?? {} };
  }
  // A SERVER ERROR IS NOT A REFUSAL (#371). See COULD_NOT_ASK.
  if (COULD_NOT_ASK.has(res.status)) return { status: 'unreachable' };
  const body = (await res.json().catch(() => null)) as { error?: unknown } | null;
  return {
    status: 'refused',
    error: typeof body?.error === 'string' ? body.error : 'unknown',
    httpStatus: res.status,
  };
}

/**
 * What is on this pad's court, as the SERVER sees it.
 *
 * Replaces mounting the club's whole dataset in the browser and filtering to
 * one court. Throws when the server cannot be reached — a pad that has been
 * rendering happily for an hour must not blank itself because one poll failed,
 * so the caller keeps the last view and tries again.
 */
export async function fetchPadState(): Promise<Record<string, unknown> | null> {
  let res: Response;
  try {
    res = await apiFetch('/pad/state', undefined, padTransportDeps() as ApiTransportDeps);
  } catch {
    throw new Error('[pad] /pad/state unreachable');
  }
  // 401 means revoked; the transport's onUnauthorized has already cleared the
  // token and the route moves to its error screen.
  if (!res.ok) return null;
  return (await res.json().catch(() => null)) as Record<string, unknown> | null;
}

/**
 * The running score of the match in front of the pad.
 *
 * FIRE AND FORGET, deliberately. It is transient and superseded by the next
 * tap, so retrying one would put an OLDER score back on the screen — and a
 * failed tap costs nothing, because the operator is still entering the match
 * and the next change carries the whole state again.
 *
 * `matchId` is what the COMMAND compares, which for an enkeltmatch is the
 * linked booking rather than the activity being drawn (see `submissionMatchId`).
 */
export async function submitPadLiveScore(matchId: string, liveScore: unknown): Promise<void> {
  try {
    await apiFetch(
      '/pad/live-score',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ matchId, liveScore }),
      },
      padTransportDeps() as ApiTransportDeps,
    );
  } catch {
    // Swallowed on purpose — see above. A dropped running score is invisible;
    // a retry storm on a failing hall link is not.
  }
}

/** The token this pad holds, if any. Re-exported so callers need one import. */
// RE-EXPORTED FOR THE PAD SURFACE, which asks «am I paired» and never «what is
// my token» (#663). The credential is httpOnly and unreachable from here.
export { isBanepadDevicePaired, markBanepadDevicePaired, retireLegacyDeviceToken };
